import type { ApiClient } from '../../shared/api/api-client';
import type { MeResponse } from './me-contract.types';

export type StaffLoginPayload = {
  email: string;
  password: string;
  /** Нормализованный телефон (7999…); обязателен для staff-входа, как в основной админке. */
  phone: string;
  /** Раздел входа: staff для CRM (сервер по умолчанию тоже staff). */
  surface?: 'staff' | 'trainer' | 'client';
  /** Студия входа (ссылка), если задана. */
  org_slug?: string | null;
  /** Токен Yandex SmartCaptcha (когда captcha_enabled=true). */
  captcha_token?: string;
};

export type AuthRequestCallResponse = {
  check_id: string;
  call_phone: string;
  call_phone_pretty?: string;
  call_phone_html?: string;
  /** Всегда 'sms_ru' (#__PLUSOFON_DISABLE__: 'plusofon' отключён). */
  provider?: 'sms_ru' | string;
  callcheck_providers?: string[];
};

export type AuthCallStatusResponse = {
  status: 'pending' | 'expired' | 'confirmed' | 'two_factor_required' | 'context_required' | string;
  token_type?: string;
  access_token?: string | null;
  refresh_token?: string | null;
  registration_token?: string | null;
  temp_token?: string;
  expires_in?: number;
  /** status=context_required: варианты раздела и одноразовый токен выбора. */
  contexts?: LoginContextOption[];
  selection_token?: string;
};

/** Вариант в окне «Войти как…». Подпись и роль формирует сервер. */
export type LoginContextOption = {
  id: string;
  surface: string;
  org_id: number;
  org_name: string;
  role: string;
  label: string;
};

export type AuthContextsResponse = {
  current: LoginContextOption | null;
  available: LoginContextOption[];
};

export type AuthContextRequiredResponse = {
  status: 'context_required';
  contexts: LoginContextOption[];
  selection_token: string;
  expires_in?: number;
};

/** Ответ входа требует выбрать раздел (несколько ролей/студий). */
export function isAuthContextRequired(
  value: StaffLoginResult | AuthLogin2FAResult | void
): value is AuthContextRequiredResponse {
  return Boolean(value && typeof value === 'object' && 'status' in value && value.status === 'context_required');
}

export type AuthMeFeedbackPayload = {
  recipient: 'developers' | 'org_admin';
  category: 'improvement' | 'complaint' | 'freeform' | 'main_admin_change';
  body_md: string;
  is_anonymous: boolean;
};

export async function postAuthRequestCall(
  client: ApiClient,
  /** provider всегда 'sms_ru' (#__PLUSOFON_DISABLE__: 'plusofon' отключён). */
  body: {
    phone: string;
    call_mode?: string;
    provider?: 'sms_ru' | string;
    captcha_token?: string;
    surface?: 'staff' | 'trainer' | 'client';
    org_slugs?: string[];
  }
): Promise<AuthRequestCallResponse> {
  return client.request<AuthRequestCallResponse>('/auth/request-call', {
    method: 'POST',
    body,
    skipAuth: true
  });
}

/** POST /auth/resend-email-verification — публичный повтор письма (гейт парольного входа). Всегда 204. */
export async function postPublicResendEmailVerification(client: ApiClient, email: string): Promise<void> {
  return client.request<void>('/auth/resend-email-verification', {
    method: 'POST',
    body: { email },
    skipAuth: true
  });
}

export async function getAuthCallStatus(client: ApiClient, checkId: string, code?: string): Promise<AuthCallStatusResponse> {
  const params: Record<string, string> = { check_id: checkId };
  if (code) {
    params.code = code;
  }
  const q = new URLSearchParams(params);
  return client.request<AuthCallStatusResponse>(`/auth/call-status?${q.toString()}`, {
    method: 'GET',
    skipAuth: true
  });
}

export type StaffLoginResponse = {
  access_token: string;
  refresh_token?: string | null;
  token_type?: string;
  user_id?: number;
  expires_in?: number;
};

export type StaffLoginResult = StaffLoginResponse | AuthContextRequiredResponse;

export async function postStaffLogin(
  client: ApiClient,
  payload: StaffLoginPayload
): Promise<StaffLoginResult> {
  return client.request<StaffLoginResult>('/auth/login', {
    method: 'POST',
    body: payload,
    skipAuth: true
  });
}

export type AuthLogin2FAResponse = {
  access_token: string;
  refresh_token?: string | null;
  token_type?: string;
  user_id?: number;
  expires_in?: number;
};

export type AuthLogin2FAResult = AuthLogin2FAResponse | AuthContextRequiredResponse;

export async function postAuthLogin2FA(
  client: ApiClient,
  body: { temp_token: string; code: string; captcha_token?: string }
): Promise<AuthLogin2FAResult> {
  return client.request<AuthLogin2FAResult>('/auth/login/2fa', {
    method: 'POST',
    body,
    skipAuth: true
  });
}

/** POST /auth/select-context — выбор раздела после подтверждения личности. */
export async function postAuthSelectContext(
  client: ApiClient,
  body: { selection_token: string; context_id: string }
): Promise<StaffLoginResponse> {
  return client.request<StaffLoginResponse>('/auth/select-context', {
    method: 'POST',
    body,
    skipAuth: true
  });
}

/** GET /auth/contexts — текущий раздел и доступные для «Сменить роль». */
export async function getAuthContexts(client: ApiClient): Promise<AuthContextsResponse> {
  return client.request<AuthContextsResponse>('/auth/contexts', { method: 'GET' });
}

/** POST /auth/switch-context — новая пара токенов, старая сессия отзывается. */
export async function postAuthSwitchContext(
  client: ApiClient,
  contextId: string
): Promise<StaffLoginResponse> {
  return client.request<StaffLoginResponse>('/auth/switch-context', {
    method: 'POST',
    body: { context_id: contextId }
  });
}

export async function getMe(
  client: ApiClient,
  options?: { accessTokenOverride?: string; superadminClearEffectiveOrg?: boolean }
): Promise<MeResponse> {
  const qs = options?.superadminClearEffectiveOrg ? '?organization_id=0' : '';
  return client.request<MeResponse>(`/auth/me${qs}`, {
    method: 'GET',
    ...(options?.accessTokenOverride
      ? { accessTokenOverride: options.accessTokenOverride }
      : {})
  });
}

/** Частичное тело PATCH `/auth/me` (см. `AuthMePatchRequest` в Go / OpenAPI). */
export type AuthMePatchPayload = Partial<{
  full_name: string | null;
  date_of_birth: string | null;
  avatar_url: string | null;
  email: string | null;
  is_parent_for_minors: boolean;
  notify_via_email: boolean;
  notify_via_telegram: boolean;
  notify_via_max: boolean;
  telegram_id: string | null;
  max_user_id: number | null;
}>;

/** PATCH `/auth/me` — ответ как у GET (обновлённый профиль). */
export async function patchAuthMe(client: ApiClient, body: AuthMePatchPayload): Promise<MeResponse> {
  return client.request<MeResponse>('/auth/me', { method: 'PATCH', body });
}

/** POST `/auth/me/change-password` — тело как в Go (`old_password`, `new_password`). */
export type AuthMeChangePasswordPayload = {
  old_password: string;
  new_password: string;
  /** Токен Yandex SmartCaptcha (когда captcha_enabled=true). */
  captcha_token?: string;
};

export async function postAuthMeChangePassword(
  client: ApiClient,
  body: AuthMeChangePasswordPayload
): Promise<Record<string, unknown>> {
  return client.request<Record<string, unknown>>('/auth/me/change-password', { method: 'POST', body });
}

export async function postAuthMeFeedback(
  client: ApiClient,
  payload: AuthMeFeedbackPayload
): Promise<Record<string, unknown>> {
  return client.request<Record<string, unknown>>('/auth/me/feedback', {
    method: 'POST',
    body: payload
  });
}

/** Снимок для привязки Telegram к учётной записи (`GET/POST /auth/me/telegram-link`). */
export type TelegramLinkResponse = {
  linked: boolean;
  pending: boolean;
  telegram_id: string | null;
  bot_username: string | null;
  connect_url: string | null;
  expires_at: string | null;
  /** false — в процессе API нет платформенного токена Telegram; UI не предлагает привязку. */
  telegram_configured?: boolean;
  /** false — нет MAX_BOT_TOKEN; UI не предлагает канал MAX. */
  max_configured?: boolean;
};

export type AuthMeTelegramLinkStartBody = {
  branch_id?: number;
};

export async function getAuthMeTelegramLink(client: ApiClient): Promise<TelegramLinkResponse> {
  return client.request<TelegramLinkResponse>('/auth/me/telegram-link', { method: 'GET' });
}

export async function postAuthMeTelegramLink(
  client: ApiClient,
  body: AuthMeTelegramLinkStartBody = {}
): Promise<TelegramLinkResponse> {
  return client.request<TelegramLinkResponse>('/auth/me/telegram-link', { method: 'POST', body });
}

/** POST `/auth/me/select-trainer-branch` — активный филиал для тренера с несколькими площадками (см. `TRAINER_BRANCH_REQUIRED`). */
export type AuthMeSelectTrainerBranchBody = {
  branch_id: number;
};

export async function postAuthMeSelectTrainerBranch(
  client: ApiClient,
  body: AuthMeSelectTrainerBranchBody
): Promise<{ ok?: boolean }> {
  return client.request<{ ok?: boolean }>('/auth/me/select-trainer-branch', { method: 'POST', body });
}

export type OnboardingResponse = {
  need_personal_data_agreement: boolean;
  need_crm_agreement: boolean;
  need_profile: boolean;
  need_org_agreement: boolean;
  personal_data_agreement_text: string;
  personal_data_agreement_version: number;
  crm_agreement_text: string;
  crm_agreement_version: number;
  org_agreement_text: string;
  org_agreement_version: number;
  personal_data_agreement_id?: number;
  crm_agreement_id?: number;
  org_agreement_id?: number | null;
  is_client_of_org: boolean | null;
};

export async function getMeOnboarding(
  client: ApiClient,
  organizationId: number | null
): Promise<OnboardingResponse> {
  const qs = organizationId ? `?organization_id=${organizationId}` : '';
  return client.request<OnboardingResponse>(`/auth/me/agreements-status${qs}`, {
    method: 'GET'
  });
}

export async function acceptAgreement(
  client: ApiClient,
  agreementId: number,
  body: { accepted_version: number; organization_id?: number }
): Promise<void> {
  return client.request<void>(`/agreements/${agreementId}/accept`, {
    method: 'POST',
    body
  });
}

export type Auth2FASetupResponse = {
  secret: string;
  uri: string;
  otpauth_url: string;
  qr_data_url?: string;
  user_id: number;
};

export async function getAuth2FASetup(client: ApiClient): Promise<Auth2FASetupResponse> {
  return client.request<Auth2FASetupResponse>('/auth/2fa/setup', { method: 'GET' });
}

export type Auth2FAConfirmResponse = {
  ok: boolean;
  two_factor_enabled: boolean;
  /** Одноразовый показ резервных кодов после включения 2FA. */
  recovery_codes?: string[];
};

export async function postAuth2FAConfirm(
  client: ApiClient,
  body: { code: string }
): Promise<Auth2FAConfirmResponse> {
  return client.request<Auth2FAConfirmResponse>('/auth/2fa/confirm', {
    method: 'POST',
    body
  });
}

export async function postAuth2FADisable(
  client: ApiClient,
  body: { code: string }
): Promise<Auth2FAConfirmResponse> {
  return client.request<Auth2FAConfirmResponse>('/auth/2fa/disable', {
    method: 'POST',
    body
  });
}